/**
 * Sign-in for the panel.
 *
 * The same endpoints the storefront uses — there is one identity system, and an
 * admin is a `User` with `role: "ADMIN"`. What differs is what happens *after*
 * a successful sign-in: the panel checks the role and refuses anyone without it
 * (see `auth-context.tsx`).
 *
 * Every call passes `skipAuth`, for the same reasons as on the storefront:
 * signing in while holding a dead token must not first try to renew it, and
 * `refresh` retrying itself is a loop.
 */
import { apiFetch, renewSession } from "@/lib/api-client";
import { clearAccessToken, setAccessToken } from "@/lib/api/tokens";
import type { ApiResponse, AuthResult } from "@/lib/api/types";

const NO_AUTH = { skipAuth: true } as const;

function adopt(result: AuthResult): AuthResult {
  setAccessToken(result.accessToken);
  return result;
}

export async function signInWithGoogle(idToken: string): Promise<AuthResult> {
  const { data } = await apiFetch<ApiResponse<AuthResult>>("/v1/auth/google", {
    ...NO_AUTH,
    body: { idToken },
  });

  return adopt(data);
}

export async function login(email: string, password: string): Promise<AuthResult> {
  const { data } = await apiFetch<ApiResponse<AuthResult>>("/v1/auth/login", {
    ...NO_AUTH,
    body: { email, password },
  });

  return adopt(data);
}

/**
 * Trades the refresh cookie for a fresh access token on a cold load.
 *
 * The access token lives in memory, so every reload starts unauthenticated as
 * far as JavaScript is concerned. Returns `null` rather than throwing — being
 * signed out is the normal case, not an error.
 */
export async function restoreSession(): Promise<AuthResult | null> {
  /**
   * Delegated rather than calling the endpoint directly, and that is the whole
   * point: `renewSession` holds the one in-flight rotation for the tab. Posting
   * to `/v1/auth/refresh` from here instead meant this call shared nothing with
   * it — so React StrictMode's double mount sent two rotations of the same
   * cookie on every cold load, and the loser's 401 could clobber the winner's
   * freshly issued cookie. It also sets and clears the access token, which is
   * what `adopt` did here.
   */
  return renewSession();
}

/**
 * Ends the session server-side and locally.
 *
 * The local token is cleared even if the request fails: someone who pressed
 * "sign out" on a shared machine must end up signed out regardless of what the
 * network did.
 */
export async function logout(): Promise<void> {
  try {
    await apiFetch("/v1/auth/logout", { ...NO_AUTH, method: "POST", body: {} });
  } finally {
    clearAccessToken();
  }
}
